import { Button } from '@/shared/components/ui/button';
import { Clock, RefreshCw, Mail } from 'lucide-react';
import type { SPClientInvitation } from '@/features/sp-client-management/types/sp-client-management.types';

type SPPendingInvitationsListProps = {
  invitations: SPClientInvitation[];
  cooldowns: Record<string, number>;
  resendingId: string | null;
  onResend: (invitationId: string) => void;
};

const getInitials = (firstName: string, lastName: string): string => {
  return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();
};

const formatRelativeDate = (dateString: string): string => {
  const date = new Date(dateString);
  const now = new Date();
  const diffInMs = now.getTime() - date.getTime();
  const diffInDays = Math.floor(diffInMs / (1000 * 60 * 60 * 24));

  if (diffInDays === 0) return 'today';
  if (diffInDays === 1) return 'yesterday';
  if (diffInDays < 7) return `${diffInDays} days ago`;
  if (diffInDays < 30) return `${Math.floor(diffInDays / 7)} weeks ago`;
  return `${Math.floor(diffInDays / 30)} months ago`;
};

const formatCooldownTime = (seconds: number): string => {
  if (seconds <= 0) return '';
  const minutes = Math.floor(seconds / 60);
  const secs = seconds % 60;
  if (minutes > 0) {
    return `${minutes}m ${secs}s`;
  }
  return `${secs}s`;
};

export function SPPendingInvitationsList({
  invitations,
  cooldowns,
  resendingId,
  onResend,
}: SPPendingInvitationsListProps) {
  return (
    <div>
      <h4 className="text-sm font-medium text-gray-700 mb-3">
        Pending Invitations ({invitations.length})
      </h4>
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        {invitations.map((invitation) => {
          const cooldownSeconds = cooldowns[invitation.id] || 0;
          const canResend = cooldownSeconds === 0 && resendingId !== invitation.id;
          const showCooldown = cooldownSeconds > 0;
          const isResending = resendingId === invitation.id;

          return (
            <div
              key={invitation.id}
              className="p-4 border border-gray-200 rounded-lg bg-gray-50"
            >
              <div className="flex items-start gap-3 mb-3">
                <div className="w-12 h-12 rounded-full bg-gray-200 flex items-center justify-center">
                  <span className="text-gray-600 font-semibold text-sm">
                    {getInitials(invitation.clientFirstName, invitation.clientLastName)}
                  </span>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <h4 className="text-sm font-medium text-gray-700 truncate">
                      {invitation.clientFirstName} {invitation.clientLastName}
                    </h4>
                    <span className="text-xs px-2 py-0.5 rounded-full bg-yellow-100 text-yellow-700">
                      Pending
                    </span>
                  </div>
                  <p className="text-xs text-gray-500 truncate">
                    {invitation.clientEmail}
                  </p>
                </div>
              </div>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-1 text-xs text-gray-500">
                  <Clock className="w-3 h-3" />
                  Invited {formatRelativeDate(invitation.createdAt)}
                </div>
                {showCooldown ? (
                  <div className="flex items-center gap-1 text-xs text-amber-600">
                    <RefreshCw className="w-3 h-3" />
                    {formatCooldownTime(cooldownSeconds)}
                  </div>
                ) : (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => onResend(invitation.id)}
                    disabled={!canResend}
                    className="h-7 px-2 text-xs text-gray-600 hover:text-[#026857] hover:bg-[#026857]/5"
                  >
                    {isResending ? (
                      <>
                        <RefreshCw className="w-3 h-3 mr-1 animate-spin" />
                        Sending...
                      </>
                    ) : (
                      <>
                        <Mail className="w-3 h-3 mr-1" />
                        Resend
                      </>
                    )}
                  </Button>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
